import { useLocale } from '@ecomm/data-hooks'
import { useOnKeyUp } from '@ecomm/shared-hooks'
import { useCartQuantity } from '@ecomm/shared-hooks'
import { useCallback } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { HeaderProps } from './types'

import { useAreExpertsAvailable } from '@ecomm/scout'
import { useCameraOnlyLPCTAExperience } from '@ecomm/shared-ninetailed'
import { usePhoneAndMonitoringInNavExperience } from '@ecomm/shared-ninetailed-phone'
import { useTrackingFullHeader } from '../../hooks/useTrackingFullHeader'
import { SideNav } from './SideNav'
import { TopNav } from './TopNav'
import { getCourtesyNavItems } from './data'

const useGetCourtesyNavItems = () => {
  const locale = useLocale()
  const { isVariant: isPhoneVariant, status } =
    usePhoneAndMonitoringInNavExperience()
  const areExpertsAvailable = useAreExpertsAvailable(8, 22)
  const { isCameraOnlyVariant } = useCameraOnlyLPCTAExperience()

  return useMemo(
    () =>
      getCourtesyNavItems(
        locale,
        isPhoneVariant,
        status,
        areExpertsAvailable,
        isCameraOnlyVariant
      ),
    [areExpertsAvailable, isPhoneVariant, locale, status, isCameraOnlyVariant]
  )
}

const useNavigationControls = () => {
  const [showSideNav, setShowSideNav] = useState(false)
  const trackFullHeader = useTrackingFullHeader()

  const toggleSideNav = useCallback(
    (track = false) => {
      trackFullHeader(track, showSideNav)
      setShowSideNav(prev => !prev)
    },
    [showSideNav, trackFullHeader]
  )

  const closeSideNav = useCallback(() => {
    setShowSideNav(false)
  }, [setShowSideNav])

  useOnKeyUp('Escape', () => {
    setShowSideNav(false)
  })

  return {
    showSideNav,
    toggleSideNav,
    closeSideNav
  }
}
const useHeaderTop = (headerRef: React.RefObject<HTMLDivElement>) => {
  const [top, setTop] = useState(0)
  useEffect(() => {
    headerRef?.current && setTop(headerRef.current.offsetTop)
  }, [headerRef])
  return top
}

type FullHeaderProps = HeaderProps & {
  readonly id?: string
}

export function FullHeader({ images, quoteWizard }: FullHeaderProps) {
  const areExpertsAvailable = useAreExpertsAvailable(8, 22)
  const headerRef = useRef<HTMLDivElement>(null)
  const top = useHeaderTop(headerRef)
  const { cartQuantity } = useCartQuantity()
  const courtesyNavItems = useGetCourtesyNavItems()
  const { showSideNav, toggleSideNav, closeSideNav } = useNavigationControls()

  return (
    <header
      className="bg-neutral-black relative w-full text-inherit lg:z-20"
      ref={headerRef}
    >
      <TopNav
        toggleSideNav={toggleSideNav}
        showSideNav={showSideNav}
        images={images}
        quoteWizard={quoteWizard}
        cartQuantity={cartQuantity}
        courtesyNavItems={courtesyNavItems}
      />
      <SideNav
        areExpertsAvailable={areExpertsAvailable}
        close={closeSideNav}
        images={images}
        quoteWizard={quoteWizard}
        show={showSideNav}
        toggle={toggleSideNav}
        top={top}
      />
    </header>
  )
}
